// Thin REST client for the Template Engine server.
const API_BASE_URL = (process.env.REACT_APP_SERVER_URL || '').replace(/\/$/, '');

export const apiUrl = (path) => `${API_BASE_URL}${path}`;

// Auth0 access-token provider, injected by App once the user is authenticated.
// When set, every request carries an Authorization: Bearer header; the server
// then derives the user from the verified token instead of the userId params.
let tokenProvider = null;
export function setApiTokenProvider(fn) { tokenProvider = fn; }

async function authHeaders(extra = {}) {
  const headers = { ...extra };
  if (tokenProvider) {
    try {
      const token = await tokenProvider();
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch (_) { /* not authenticated / silent auth failed — send without */ }
  }
  return headers;
}

async function handle(res) {
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json()).error || ''; } catch (_) {}
    throw new Error(detail || `Request failed (${res.status})`);
  }
  return res.status === 204 ? null : res.json();
}

async function call(path, { headers, ...opts } = {}) {
  const res = await fetch(apiUrl(path), { ...opts, headers: await authHeaders(headers) });
  return handle(res);
}

/**
 * Call the external /api/v1 surface with an API key, the way an integrator's
 * server would. Unlike call() this NEVER throws and always reports the wire
 * result — the wizard shows status codes and timings as content, including the
 * failures, so a thrown exception would hide the very thing being demonstrated.
 *
 * Returns { ok, status, ms, body, error }.
 */
export async function callV1(path, { method = 'GET', body, key } = {}) {
  const started = performance.now();
  try {
    const res = await fetch(apiUrl(path), {
      method,
      headers: {
        ...(key ? { 'X-API-Key': key } : {}),
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(body ? { body: typeof body === 'string' ? body : JSON.stringify(body) } : {}),
    });
    const ms = Math.round(performance.now() - started);
    let parsed = null;
    try { parsed = await res.json(); } catch (_) { /* empty or non-JSON body */ }
    return { ok: res.ok, status: res.status, ms, body: parsed };
  } catch (e) {
    // Network-level failure: no status to report, so say so rather than inventing one.
    return {
      ok: false, status: 0, ms: Math.round(performance.now() - started),
      body: null, error: e.message || 'Network error',
    };
  }
}

export const api = {
  // Demo screens — throwaway devices for the wizard. Exempt from the tier
  // screen limit and reaped server-side; see POST /api/demo/screens.
  createDemoScreens: (count, userId, userEmail) =>
    call('/api/demo/screens', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ count, userId, userEmail })
    }),
  deleteDemoScreens: (userId) =>
    call(`/api/demo/screens?userId=${encodeURIComponent(userId || '')}`, { method: 'DELETE' }),

  // Templates
  listTemplates: (userId) =>
    call(`/api/templates?userId=${encodeURIComponent(userId || '')}`),
  getTemplate: (id) => call(`/api/templates/${id}`),
  createTemplate: (body) =>
    call('/api/templates', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    }),
  updateTemplate: (id, body) =>
    call(`/api/templates/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    }),
  deleteTemplate: (id) =>
    call(`/api/templates/${id}`, { method: 'DELETE' }),
  uploadTemplate: (file, userId, name) => {
    const fd = new FormData();
    fd.append('file', file);
    if (userId) fd.append('userId', userId);
    if (name) fd.append('name', name);
    return call('/api/templates/upload', { method: 'POST', body: fd });
  },

  // Devices
  listDevices: (userId) =>
    call(`/api/devices?userId=${encodeURIComponent(userId || '')}`),
  unclaimDevice: (deviceUUID, userId) =>
    call('/api/device/unclaim', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deviceUUID, userId })
    }),
  pushToDevice: (deviceUUID, templateId, variables) =>
    call(`/api/devices/${encodeURIComponent(deviceUUID)}/push`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ templateId, variables })
    }),
  clearDevice: (deviceUUID) =>
    call(`/api/devices/${encodeURIComponent(deviceUUID)}/clear`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}'
    }),

  renameDevice: (deviceUUID, body) =>
    call(`/api/devices/${encodeURIComponent(deviceUUID)}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }),

  // API keys — the plaintext key comes back from createApiKey ONCE and is
  // never retrievable again; listApiKeys only ever returns prefixes.
  listApiKeys: (userId) =>
    call(`/api/keys?userId=${encodeURIComponent(userId || '')}`),
  createApiKey: (name, userId, userEmail) =>
    call('/api/keys', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, userId, userEmail })
    }),
  revokeApiKey: (id, userId) =>
    call(`/api/keys/${encodeURIComponent(id)}?userId=${encodeURIComponent(userId || '')}`,
      { method: 'DELETE' }),

  // Pairing
  claimPairing: (token, userId, userEmail) =>
    call('/api/pair/claim', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, userId, userEmail })
    }),

  // Floor plans
  listFloorplans: (userId) =>
    call(`/api/floorplans?userId=${encodeURIComponent(userId || '')}`),
  createFloorplan: (name, userId) =>
    call('/api/floorplans', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, userId })
    }),
  uploadFloorplanImage: (id, file) => {
    const fd = new FormData();
    fd.append('image', file);
    return call(`/api/floorplans/${id}/image`, { method: 'POST', body: fd });
  },
  saveFloorplanDevices: (id, placements) =>
    call(`/api/floorplans/${id}/devices`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ placements })
    }),
  deleteFloorplan: (id) =>
    call(`/api/floorplans/${id}`, { method: 'DELETE' }),

  // Billing (Chargebee) — userId only matters in dev mode; with Auth0 the
  // server derives the user from the Bearer token.
  billingStatus: (userId) =>
    call(`/api/billing/status?userId=${encodeURIComponent(userId || '')}`),
  billingCheckout: (quantity, userId, currency) =>
    call('/api/billing/checkout', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ quantity, userId, currency })
    }),
  // Cancels at end of term; the server writes through immediately so the next
  // status fetch already reflects it.
  billingCancel: (userId) =>
    call('/api/billing/cancel', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId })
    }),
  billingPortal: (userId) =>
    call('/api/billing/portal', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId })
    }),

  // Data sources
  listDataSources: (userId) =>
    call(`/api/data-sources?userId=${encodeURIComponent(userId || '')}`),
  getDataSource: (id) => call(`/api/data-sources/${id}`),
  createDataSource: (body) =>
    call('/api/data-sources', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    }),
  updateDataSource: (id, body) =>
    call(`/api/data-sources/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    }),
  deleteDataSource: (id) =>
    call(`/api/data-sources/${id}`, { method: 'DELETE' }),
  fetchDataSource: (id, userId) =>
    call(`/api/data-sources/${id}/fetch`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId })
    }),
};
